import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Alert,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Mail,
  EventNote,
  ReportProblem,
  LocalActivity,
  NotificationsNone,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from '../api/axios';
import { formatDate } from '../utils/format';

interface NotificationItem {
  id: string;
  type: 'invitation' | 'booking' | 'problem' | 'activity';
  houseId: string;
  houseName: string;
  title: string;
  status: string;
  actorName: string | null;
  createdAt: string;
  updatedAt: string;
}

interface NotificationsData {
  invitations: NotificationItem[];
  bookings: NotificationItem[];
  problems: NotificationItem[];
  activities: NotificationItem[];
  totalCount: number;
}

type TabKey = 'all' | 'invitations' | 'bookings' | 'problems' | 'activities';

const statusColorMap: Record<string, 'warning' | 'success' | 'error' | 'info' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  accepted: 'success',
  rejected: 'error',
  reported: 'warning',
  in_progress: 'info',
  resolved: 'success',
  cancelled: 'default',
};

const statusKeyMap: Record<string, string> = {
  pending: 'common.statuses.pending',
  approved: 'common.statuses.approved',
  accepted: 'common.statuses.accepted',
  rejected: 'common.statuses.rejected',
  reported: 'common.statuses.reported',
  in_progress: 'common.statuses.inProgress',
  resolved: 'common.statuses.resolved',
  cancelled: 'common.statuses.cancelled',
};

const typeIconMap: Record<string, React.ReactElement> = {
  invitation: <Mail fontSize="small" />,
  booking: <EventNote fontSize="small" />,
  problem: <ReportProblem fontSize="small" />,
  activity: <LocalActivity fontSize="small" />,
};

const NotificationsPage: React.FC = () => {
  const [data, setData] = useState<NotificationsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('all');
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/notifications');
        setData(response.data);
      } catch {
        setError(t('notifications.failedFetch'));
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [t]);

  const getVisibleItems = (): NotificationItem[] => {
    if (!data) return [];

    if (tab === 'all') {
      return [
        ...data.invitations,
        ...data.bookings,
        ...data.problems,
        ...data.activities,
      ].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }

    return data[tab];
  };

  const handleItemClick = (item: NotificationItem) => {
    switch (item.type) {
      case 'invitation':
        navigate('/invitations');
        break;
      case 'booking':
        navigate(`/houses/${item.houseId}/calendar`);
        break;
      case 'problem':
        navigate(`/houses/${item.houseId}/problems`);
        break;
      case 'activity':
        navigate(`/houses/${item.houseId}/activities`);
        break;
    }
  };

  const getItemDescription = (item: NotificationItem): string => {
    switch (item.type) {
      case 'invitation':
        return item.actorName
          ? t('notifications.invitationFrom', { name: item.actorName, house: item.houseName })
          : t('notifications.invitationForHouse', { house: item.houseName });
      case 'booking':
        if (item.status === 'pending') {
          return item.actorName
            ? t('notifications.bookingRequest', { name: item.actorName, house: item.houseName })
            : t('notifications.bookingRequestHouse', { house: item.houseName });
        }
        return t('notifications.bookingStatusChange', { house: item.houseName });
      case 'problem':
        if (item.status === 'reported' && item.actorName) {
          return t('notifications.problemReportedBy', { name: item.actorName, title: item.title, house: item.houseName });
        }
        return item.status === 'reported'
          ? t('notifications.problemReported', { title: item.title, house: item.houseName })
          : t('notifications.problemStatusChange', { title: item.title, house: item.houseName });
      case 'activity':
        if (item.status === 'pending' && item.actorName) {
          return t('notifications.activitySuggestedBy', { name: item.actorName, title: item.title, house: item.houseName });
        }
        return item.status === 'pending'
          ? t('notifications.activitySuggested', { title: item.title, house: item.houseName })
          : t('notifications.activityStatusChange', { title: item.title, house: item.houseName });
      default:
        return '';
    }
  };

  const tabCounts = data
    ? {
      all: data.totalCount,
      invitations: data.invitations.length,
      bookings: data.bookings.length,
      problems: data.problems.length,
      activities: data.activities.length,
    }
    : { all: 0, invitations: 0, bookings: 0, problems: 0, activities: 0 };

  const items = getVisibleItems();

  return (
    <Layout title={t('notifications.layoutTitle')}>
      <Container maxWidth="md">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1">
            {t('notifications.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('notifications.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value as TabKey)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 3 }}
            >
              <Tab label={`${t('notifications.tabs.all')} (${tabCounts.all})`} value="all" />
              <Tab label={`${t('notifications.tabs.invitations')} (${tabCounts.invitations})`} value="invitations" />
              <Tab label={`${t('notifications.tabs.bookings')} (${tabCounts.bookings})`} value="bookings" />
              <Tab label={`${t('notifications.tabs.problems')} (${tabCounts.problems})`} value="problems" />
              <Tab label={`${t('notifications.tabs.activities')} (${tabCounts.activities})`} value="activities" />
            </Tabs>

            {items.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <NotificationsNone sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('notifications.empty')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications.emptyDescription')}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {items.map((item) => (
                  <Card key={`${item.type}-${item.id}`} variant="outlined">
                    <CardActionArea onClick={() => handleItemClick(item)}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                        <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                          {typeIconMap[item.type]}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
                            {getItemDescription(item)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(item.updatedAt, i18n.resolvedLanguage)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                          <Chip
                            label={t(`notifications.types.${item.type}`)}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            label={t(statusKeyMap[item.status] || item.status)}
                            color={statusColorMap[item.status] || 'default'}
                            size="small"
                          />
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            )}
          </>
        )}
      </Container>
    </Layout>
  );
};

export default NotificationsPage;
